/**
 * @fileoverview Admin Sidebar
 *
 * Purpose: Dedicated sidebar navigation for the admin panel.
 * Separate from the main dashboard sidebar to create a clear visual distinction.
 *
 * Navigation items:
 * - Overview (/admin)
 * - Invitations (/admin/invitations)
 * - Users (/admin/users)
 * - Organisations (/admin/organisations)
 * - Back to App (/dashboard)
 *
 * Dependencies:
 * - shadcn Sidebar components (src/ui/layout/sidebar.tsx)
 * - Lucide icons
 *
 * Used by:
 * - Admin layout (_layout.tsx)
 */

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/ui/layout/sidebar";
import { Logo } from "@/ui/layout/logo";
import { cn } from "@/utils/misc";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  LayoutDashboard,
  Mail,
  MailPlus,
  Users,
  Building2,
  ArrowLeft,
  Shield,
  FileSearch,
  ScrollText,
  Sparkles,
  Library,
  CircleDollarSign,
  ReceiptText,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Navigation Configuration
// ---------------------------------------------------------------------------

const adminNavItems = [
  {
    title: "Overview",
    url: "/admin",
    icon: LayoutDashboard,
    exact: true,
  },
  {
    title: "Invitations",
    url: "/admin/invitations",
    icon: Mail,
    exact: false,
  },
  {
    title: "Users",
    url: "/admin/users",
    icon: Users,
    exact: false,
  },
  {
    title: "Organisations",
    url: "/admin/organisations",
    icon: Building2,
    exact: false,
  },
  {
    title: "Master Library",
    url: "/admin/master-library",
    icon: Library,
    exact: false,
  },
  {
    title: "InfoTrack Searches",
    url: "/admin/infotrack",
    icon: ScrollText,
    exact: false,
  },
  {
    title: "Reducto Jobs",
    url: "/admin/reducto",
    icon: FileSearch,
    exact: false,
  },
  {
    title: "AI Usage",
    url: "/admin/ai-usage",
    icon: Sparkles,
    exact: false,
  },
  {
    title: "Cost Management",
    url: "/admin/cost-management",
    icon: CircleDollarSign,
    exact: false,
  },
  {
    title: "Billing",
    url: "/admin/billing",
    icon: ReceiptText,
    exact: false,
  },
  {
    title: "Email Templates",
    url: "/admin/email-templates",
    icon: MailPlus,
    exact: false,
  },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Admin panel sidebar with navigation links and "Back to App" footer.
 */
export function AdminSidebar() {
  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="p-4">
        <AdminLogoHeader />
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        <AdminNav />
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter className="p-2">
        <BackToApp />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// ---------------------------------------------------------------------------
// Logo Header
// ---------------------------------------------------------------------------

/**
 * Logo with "Admin" badge in the sidebar header.
 */
function AdminLogoHeader() {
  return (
    <Link to="/admin" className="flex items-center gap-2">
      <Logo
        compact
        className="h-8 w-8 shrink-0 text-primary group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6"
      />
      <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
        <span className="truncate font-serif text-lg font-medium text-primary">
          Morgan
        </span>
        <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          <Shield className="h-3 w-3" />
          Admin
        </span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * Admin navigation menu items.
 */
function AdminNav() {
  const matchRoute = useMatchRoute();
  const { data: user } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  // Non-owner @morgan.so admins may only see Master Library.
  const navItems = user?.isOwner
    ? adminNavItems
    : adminNavItems.filter((item) => item.url === "/admin/master-library");

  return (
    <SidebarGroup className="p-2">
      <SidebarGroupLabel className="px-2 text-xs font-medium text-primary/50">
        Administration
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {navItems.map((item) => {
            const isActive = item.exact
              ? matchRoute({ to: item.url, fuzzy: false })
              : matchRoute({ to: item.url, fuzzy: true });

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={!!isActive}
                  tooltip={item.title}
                >
                  <Link
                    to={item.url}
                    className={cn(
                      isActive ? "font-medium text-primary" : "text-primary/60",
                    )}
                  >
                    <item.icon className="size-4" />
                    <span className="text-sm">{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// ---------------------------------------------------------------------------
// Back to App
// ---------------------------------------------------------------------------

/**
 * "Back to App" link in the sidebar footer.
 */
function BackToApp() {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Back to App">
          <Link to="/dashboard" className="text-primary/60 hover:text-primary">
            <ArrowLeft className="size-4" />
            <span className="text-sm">Back to App</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
